import { Text, Button, TextField, Select, IconButton } from 'frosted-ui';
import { Plus16, Trash16 } from '@frosted-ui/icons';
import {
  formatCurrency,
  getCurrencySymbol,
  formatAmount,
  toMinorUnits,
  getPriceInputStep,
} from '../../utils/currency';
import { nextChallengerPrice } from '../../utils/suggestions';
import { variantLabel } from '../../utils/variants';
import { evenSplit } from './unlockDraft';
import { priceProblem } from './priceTest';
import VariantPromoPicker from './VariantPromoPicker';
import type { WizardData, WizardVariant } from './wizardTypes';

/** One price, in the currency's major units, with what's wrong with it if anything. */
export function PriceField({
  label,
  hint,
  variant,
  currency,
  onChange,
  onRemove,
}: {
  label: string;
  hint?: string;
  variant: WizardVariant;
  currency: string;
  onChange: (price: number) => void;
  onRemove?: () => void;
}) {
  const problem = priceProblem(variant.price, currency);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <Text as="label" size="2" weight="medium">
          {label}
        </Text>
        {onRemove && (
          <IconButton type="button" variant="ghost" color="gray" size="1" aria-label={`Remove ${label}`} onClick={onRemove}>
            <Trash16 />
          </IconButton>
        )}
      </div>
      <TextField.Root size="3" variant="surface" color={problem ? 'red' : undefined}>
        <TextField.Slot>
          <Text size="2" color="gray">{getCurrencySymbol(currency)}</Text>
        </TextField.Slot>
        <TextField.Input
          aria-label={label}
          type="number"
          min="0"
          step={getPriceInputStep(currency)}
          value={formatAmount(variant.price, currency)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(toMinorUnits(parseFloat(e.target.value) || 0, currency))
          }
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            e.target.value = (parseFloat(e.target.value) || 0).toString();
          }}
        />
      </TextField.Root>
      <Text size="1" color={problem ? 'red' : 'gray'} style={{ display: 'block', marginTop: 'var(--space-1)' }}>
        {problem ??
          hint ??
          `${formatCurrency(variant.price, currency)}${variant.interval !== 'one_time' ? `/${variant.interval}` : ' once'}`}
      </Text>
    </div>
  );
}

interface MoreOptionsProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  /** Whop's in-app checkout can't apply a promo code. */
  promosAllowed: boolean;
  /** Step 1 already shows the Control field (nothing to copy it from). */
  controlShownAbove?: boolean;
}

/**
 * Everything behind "More options", with safe defaults: the Control price, a
 * third price, billing and free trial (set once for every price: revenue per
 * visitor can only compare prices), promo codes and the split. Variants keep
 * their code names (A/B/C); A is the Control.
 */
export default function VariantsEditor({ data, onChange, promosAllowed, controlShownAbove = false }: MoreOptionsProps) {
  const control = data.variants[0];
  const third = data.variants[2];

  function updateVariant(index: number, updates: Partial<WizardVariant>) {
    onChange({ variants: data.variants.map((v, i) => (i === index ? { ...v, ...updates } : v)) });
  }

  function updateAll(updates: Partial<WizardVariant>) {
    onChange({ variants: data.variants.map((v) => ({ ...v, ...updates })) });
  }

  function addThirdPrice() {
    const last = data.variants[data.variants.length - 1];
    const variants = [
      ...data.variants,
      { ...last, name: 'C', price: nextChallengerPrice(data.variants.map((v) => v.price)) },
    ].map((v) => (v.name === 'C' ? clearPromo(v) : v));
    onChange({ variants, split: evenSplit(variants.length) });
  }

  function removeThirdPrice() {
    const variants = data.variants.slice(0, 2);
    onChange({ variants, split: evenSplit(variants.length) });
  }

  const splitSum = data.variants.reduce((sum, v) => sum + (data.split[v.name] ?? 0), 0);

  return (
    <div className="vara2-step-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="variant-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        {!controlShownAbove && (
          <PriceField
            label="Control price"
            hint="Keep this at what buyers pay today."
            variant={control}
            currency={data.currency}
            onChange={(price) => updateVariant(0, { price })}
          />
        )}
        {third && (
          <PriceField
            label={variantLabel('C')}
            variant={third}
            currency={data.currency}
            onChange={(price) => updateVariant(2, { price })}
            onRemove={removeThirdPrice}
          />
        )}
      </div>
      {!third && (
        <div>
          <Button type="button" variant="soft" color="gray" size="2" onClick={addThirdPrice}>
            <Plus16 />
            Add a third price
          </Button>
        </div>
      )}

      <div className="variant-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        <div>
          <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            Billing (every price)
          </Text>
          <Select.Root
            value={control.interval}
            onValueChange={(value: string) => {
              const interval = value as WizardVariant['interval'];
              updateAll(interval === 'one_time' ? { interval, trialDays: 0 } : { interval });
            }}
            size="2"
          >
            <Select.Trigger variant="surface" style={{ width: '100%' }} />
            <Select.Content>
              <Select.Item value="week">Weekly</Select.Item>
              <Select.Item value="month">Monthly</Select.Item>
              <Select.Item value="year">Yearly</Select.Item>
              <Select.Item value="one_time">One-time</Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
        {control.interval !== 'one_time' && (
          <div>
            <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              Free trial, days (every price)
            </Text>
            <TextField.Root size="2" variant="surface">
              <TextField.Input
                type="number"
                min="0"
                value={control.trialDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateAll({ trialDays: Math.max(parseInt(e.target.value) || 0, 0) })
                }
              />
            </TextField.Root>
          </div>
        )}
      </div>

      <div>
        <Text size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-1)' }}>
          Promo codes
        </Text>
        {promosAllowed ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {data.variants.map((v, index) => (
              <div key={v.name}>
                <Text size="1" color="gray" style={{ display: 'block' }}>
                  {variantLabel(v.name)}
                </Text>
                <VariantPromoPicker
                  companyId={data.companyId}
                  productId={data.productId}
                  selectedPromoId={v.promoCodeId}
                  selectedPromoCode={v.promoCode}
                  onChange={(promo) =>
                    updateVariant(index, {
                      promoCodeId: promo?.id,
                      promoCode: promo?.code,
                      promoAmountOff: promo?.amountOff,
                      promoType: promo?.promoType,
                      promoDuration: promo?.duration ?? undefined,
                      promoDurationMonths: promo?.promoDurationMonths,
                    })
                  }
                />
              </div>
            ))}
          </div>
        ) : (
          <Text size="2" color="gray">
            Not available inside your whop: Whop's in-app checkout doesn't apply promo codes. Use a
            link you share to test one.
          </Text>
        )}
      </div>

      <div>
        <Text size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-1)' }}>
          Share of visitors for each price
        </Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {data.variants.map((v) => (
            <div key={v.name} className="traffic-split-item">
              <Text size="2" className="traffic-split-label">
                {variantLabel(v.name)}
              </Text>
              <div className="traffic-split-controls">
                <input
                  type="range"
                  min="0"
                  max="100"
                  aria-label={`${variantLabel(v.name)} share`}
                  style={{ flex: 1, minWidth: 0 }}
                  value={data.split[v.name] ?? 0}
                  onChange={(e) =>
                    onChange({ split: { ...data.split, [v.name]: parseInt(e.target.value) } })
                  }
                />
                <TextField.Root size="2" variant="surface" style={{ width: 72, flexShrink: 0 }}>
                  <TextField.Input
                    type="number"
                    min="0"
                    max="100"
                    aria-label={`${variantLabel(v.name)} percent`}
                    value={data.split[v.name] ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange({ split: { ...data.split, [v.name]: parseInt(e.target.value) || 0 } })
                    }
                  />
                </TextField.Root>
                <Text size="2" color="gray" style={{ flexShrink: 0 }}>%</Text>
              </div>
            </div>
          ))}
        </div>
        {splitSum !== 100 && (
          <Text size="2" color="red" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
            Total is {splitSum}%. It has to add up to 100%.
          </Text>
        )}
      </div>

      <div>
        <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
          Test name
        </Text>
        <TextField.Root size="2" variant="surface">
          <TextField.Input
            aria-label="Test name"
            value={data.name}
            maxLength={200}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ name: e.target.value })}
          />
        </TextField.Root>
        <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
          Only you see this.
        </Text>
      </div>
    </div>
  );
}

function clearPromo(variant: WizardVariant): WizardVariant {
  const { promoCodeId, promoCode, promoAmountOff, promoType, promoDuration, promoDurationMonths, ...rest } = variant;
  void [promoCodeId, promoCode, promoAmountOff, promoType, promoDuration, promoDurationMonths];
  return rest;
}
